import * as XLSX from 'xlsx';
import { tableFromIPC } from 'apache-arrow';

export type NormalisedFile = { fileName: string; format: string; bytes: Uint8Array; originalBytes: Uint8Array; originalName: string; mime: string };

export function safeTableName(fileName: string, existing: string[] = []): string {
  const base = fileName.replace(/\.[^.]+$/, '').normalize('NFKD').replace(/[^a-zA-Z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'data';
  const prefixed = /^\d/.test(base) ? `t_${base}` : base;
  let candidate = prefixed;
  let counter = 2;
  while (existing.includes(candidate)) candidate = `${prefixed}_${counter++}`;
  return candidate;
}

export function detectFormat(name: string): string {
  const extension = name.toLowerCase().split('.').pop();
  const formats: Record<string, string> = { csv:'csv',tsv:'tsv',json:'json',jsonl:'ndjson',ndjson:'ndjson',parquet:'parquet',arrow:'arrow',feather:'arrow',xlsx:'xlsx',xls:'xlsx',geojson:'geojson' };
  const format = extension ? formats[extension] : undefined;
  if (!format) throw new Error(`Unsupported file type: .${extension ?? ''}`);
  return format;
}

function rowsToCsv(rows: Array<Record<string, unknown>>): Uint8Array {
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const escape = (value: unknown) => {
    const text = value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"','""')}"` : text;
  };
  return new TextEncoder().encode([columns.join(','), ...rows.map((row) => columns.map((column) => escape(row[column])).join(','))].join('\n'));
}

export async function normaliseFile(file: File): Promise<NormalisedFile> {
  const originalBytes = new Uint8Array(await file.arrayBuffer());
  const format = detectFormat(file.name);
  if (format === 'xlsx') {
    const workbook = XLSX.read(originalBytes, { type: 'array', cellDates: true });
    const first = workbook.SheetNames[0];
    if (!first) throw new Error('The workbook has no worksheets.');
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[first]!);
    return { fileName: `${file.name.replace(/\.[^.]+$/,'')}.csv`, format: 'csv', bytes: new TextEncoder().encode(csv), originalBytes, originalName: file.name, mime: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
  }
  if (format === 'geojson') {
    const parsed = JSON.parse(new TextDecoder().decode(originalBytes)) as { type?: string; features?: Array<{ properties?: Record<string, unknown>; geometry?: unknown }> };
    if (parsed.type === 'FeatureCollection' && parsed.features) {
      const rows = parsed.features.map((feature) => ({ ...(feature.properties ?? {}), geometry: feature.geometry }));
      const bytes = new TextEncoder().encode(JSON.stringify(rows));
      return { fileName: `${file.name}.json`, format: 'json', bytes, originalBytes, originalName: file.name, mime: file.type || 'application/geo+json' };
    }
  }
  if (format === 'arrow') {
    try { tableFromIPC(originalBytes); } catch { throw new Error('The Arrow/Feather file could not be decoded.'); }
  }
  return { fileName: file.name, format, bytes: originalBytes, originalBytes, originalName: file.name, mime: file.type || 'application/octet-stream' };
}

export function fileFromRemote(bytes: Uint8Array, name: string, mime = 'application/octet-stream'): File {
  return new File([bytes as BlobPart], name, { type: mime });
}

export function resultToCsv(columns: string[], rows: Array<Record<string, unknown>>): string {
  const escape = (value: unknown) => {
    const text = value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"','""')}"` : text;
  };
  return [columns.map(escape).join(','), ...rows.map((row) => columns.map((column) => escape(row[column])).join(','))].join('\n');
}

export function downloadBytes(bytes: Uint8Array | string, fileName: string, type = 'application/octet-stream'): void {
  const blob = new Blob([bytes as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = fileName; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
